import { Button, Card, Flex, Grid, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { validateUrlProtocol } from '../../../helpers/validateURL';
import LabeledSlider from '../../LabeledSlider';
import ProductFeedSelector from '../../ProductFeeds/ProductFeedSelector';
import GoogleAssetSelector from './GoogleAssetSelector';
import GoogleSearchThemesInput from './GoogleSearchThemesInput';

const GoogleAssetGroupConfigurationTab = ({ campaign, handleChange, onAssetValidationChange }) => {
  const { accountId, domainId } = useParams();
  const [finalUrls, setFinalUrls] = useState(
    Array.isArray(campaign.googleAssetGroup?.finalUrls) && campaign.googleAssetGroup.finalUrls.length > 0
      ? campaign.googleAssetGroup.finalUrls
      : ['']
  );

  const [finalMobileUrls, setFinalMobileUrls] = useState(
    Array.isArray(campaign.googleAssetGroup?.finalMobileUrls) && campaign.googleAssetGroup.finalMobileUrls.length > 0
      ? campaign.googleAssetGroup.finalMobileUrls
      : ['']
  );
  const [finalUrlErrors, setFinalUrlErrors] = useState(finalUrls.map(() => false));
  const [finalMobileUrlErrors, setFinalMobileUrlErrors] = useState(finalMobileUrls.map(() => false));
  const [pathOneMissing, setPathOneMissing] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(campaign.googleAssetGroup?.selectedAssets || []);

  const updateUrls = (type, newUrls) => {
    if (type === 'finalUrls') {
      setFinalUrls(newUrls);
      setFinalUrlErrors(newUrls.map((url) => !validateUrlProtocol(url)));
    } else if (type === 'finalMobileUrls') {
      setFinalMobileUrls(newUrls);
      setFinalMobileUrlErrors(newUrls.map((url) => !validateUrlProtocol(url)));
    }
    handleChange({ target: { name: `googleAssetGroup.${type}`, value: newUrls } });
  };

  const addUrl = (type) => {
    const newUrls = type === 'finalUrls' ? [...finalUrls, ''] : [...finalMobileUrls, ''];
    updateUrls(type, newUrls);
  };

  const removeUrl = (type, index) => {
    const newUrls = (type === 'finalUrls' ? finalUrls : finalMobileUrls).filter((_, i) => i !== index);
    updateUrls(type, newUrls);
  };

  const handleUrlChange = (type, index, value) => {
    const newUrls = (type === 'finalUrls' ? finalUrls : finalMobileUrls).map((url, i) => (i === index ? value : url));
    updateUrls(type, newUrls);
  };

  const getDisplayURL = (url) => {
    try {
      return new URL(url).origin;
    } catch (e) {
      console.error(e);
      return 'www.example.com';
    }
  };

  const handleAssetSelect = (assets) => {
    setSelectedAssets(assets);
    handleChange({ target: { name: 'googleAssetGroup.selectedAssets', value: assets } });
  };

  return (
    <>
      <GoogleAssetSelector selectedAssets={selectedAssets} handleAssetSelect={handleAssetSelect} onValidationChange={onAssetValidationChange} />
      <GoogleSearchThemesInput
        value={campaign.googleAssetGroup?.searchThemes || []}
        onChange={(searchThemes) =>
          handleChange({ target: { name: 'googleAssetGroup.searchThemes', value: searchThemes } })
        }
      />
      {campaign.googleAssetGroup?.isRetail && (
        <Card withBorder>
          <Flex component={Card.Section} inheritPadding py="md" bg="white" justify="space-between" align="center">
            <Text fw={500} size="lg" pt="xs" mb={0}>
              Listing Groups
            </Text>
          </Flex>
          <Card.Section inheritPadding py="md">
            <Grid align="center" mb="sm">
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Text fw={500}>Products to include*</Text>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <SegmentedControl
                  value={campaign.googleAssetGroup.productSet || 'ranked'}
                  onChange={(value) => handleChange({ target: { name: 'googleAssetGroup.productSet', value } })}
                  data={[
                    { label: 'All products', value: 'full' },
                    { label: 'Ranked subset', value: 'ranked' },
                  ]}
                />
                <Text size="sm" c="dimmed">
                  Ranked subset features a tailored selection of products, optimized for a specific segment.
                </Text>
              </Grid.Col>
            </Grid>
            {campaign.googleAssetGroup.productSet !== 'full' && (
              <>
                <Grid align="center" mb="sm">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text fw={500}>Product Feed</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <ProductFeedSelector
                      accountId={accountId}
                      domainId={domainId}
                      productFeedId={parseInt(campaign.googleAssetGroup.productFeedId)}
                      onSelect={(productFeedId) =>
                        handleChange({ target: { name: 'googleAssetGroup.productFeedId', value: productFeedId } })
                      }
                      forType="google product set"
                      scope="marketingChannels"
                    />
                  </Grid.Col>
                </Grid>

                <Grid align="center" mb="sm">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text fw={500}>Max products to include</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <LabeledSlider
                      min={10}
                      max={990}
                      value={campaign.googleAssetGroup.count}
                      onChange={(count) => handleChange({ target: { name: 'googleAssetGroup.count', value: count } })}
                    />
                  </Grid.Col>
                </Grid>
              </>
            )}
          </Card.Section>
        </Card>
      )}
      <Card withBorder>
        <Card.Section inheritPadding py="md">
          <Grid align="center" mb="sm">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text fw={500}>Final URLs</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              {finalUrls.map((url, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 10 }}>
                      <TextInput
                        value={url}
                        placeholder="Enter final URL"
                        error={
                          finalUrlErrors[index]
                            ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
                            : null
                        }
                        onChange={(e) => {
                          handleUrlChange('finalUrls', index, e.target.value);
                        }}
                      />
                      <Text size="sm" c="dimmed">
                        A Final URL is the webpage that users end up on after they click on your ad.{' '}
                        <strong>
                          Note that the final URL must match the domain verified in your Merchant Center account if it
                          is a Performance Max Retail.
                        </strong>
                        .
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      {index > 0 && (
                        <Button onClick={() => removeUrl('finalUrls', index)} color="red">
                          <IconX size={16} />
                        </Button>
                      )}
                    </Grid.Col>
                  </Grid>
                </div>
              ))}
              <Button onClick={() => addUrl('finalUrls')}>Add Final URL</Button>
            </Grid.Col>
          </Grid>
          <Grid align="center" mb="sm">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text fw={500}>Final Mobile URLs</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 10 }}>
              {finalMobileUrls.map((url, index) => (
                <div key={index} style={{ marginBottom: '8px' }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 10 }}>
                      <TextInput
                        value={url}
                        error={
                          finalMobileUrlErrors[index]
                            ? 'Invalid URL. Please check the address for typing errors or missing protocol.'
                            : null
                        }
                        placeholder="Enter final mobile URL"
                        onChange={(e) => handleUrlChange('finalMobileUrls', index, e.target.value)}
                      />
                      <Text size="sm" c="dimmed">
                        A Final Mobile URL is the webpage that users end up on after they click on your ad.{' '}
                        <strong>
                          Note that the Final Mobile URL must match the domain verified in your Merchant Center account
                          if it is a Performance Max Retail.
                        </strong>
                        .
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      {index > 0 && (
                        <Button onClick={() => removeUrl('finalMobileUrls', index)} color="red">
                          <IconX size={16} />
                        </Button>
                      )}
                    </Grid.Col>
                  </Grid>
                </div>
              ))}
              <Button onClick={() => addUrl('finalMobileUrls')}>Add Final Mobile URL</Button>
            </Grid.Col>
          </Grid>
          <Grid align="center" mb="sm">
            <Grid.Col span={{ base: 12, sm: 2 }}>
              <Text fw={500}>Display path</Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 2 }}>{getDisplayURL(finalUrls[0])}</Grid.Col>
            <Grid.Col span="content">{'/'}</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <TextInput
                name="pathOne"
                value={campaign.googleAssetGroup?.pathOne || ''}
                placeholder={`e.g. shoes`}
                error={campaign.googleAssetGroup?.pathOne?.length > 15 ? 'Value too long' : null}
                onChange={(e) => handleChange({ target: { name: `googleAssetGroup.pathOne`, value: e.target.value } })}
              />

              <Text size="sm" c="dimmed">
                <strong>{campaign.googleAssetGroup?.pathOne?.length || '0 '}/15</strong> First part of text that may
                appear appended to the <strong>displayed URL</strong> in the ad. For example, if your site is
                https://example.com, setting path1 to "shoes" could result in a display URL like
                https://example.com/shoes
              </Text>
            </Grid.Col>
            <Grid.Col span="content">{'/'}</Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }}>
              <TextInput
                name="pathTwo"
                value={campaign.googleAssetGroup?.pathTwo || ''}
                placeholder={`e.g. men`}
                error={
                  pathOneMissing || campaign.googleAssetGroup?.pathTwo?.length > 15
                    ? 'The value is too long, or this field can only be set when path one is specified.'
                    : null
                }
                onChange={(e) => {
                  setPathOneMissing(!campaign.googleAssetGroup?.pathOne);
                  handleChange({ target: { name: `googleAssetGroup.pathTwo`, value: e.target.value } });
                }}
              />
              <Text size="sm" c="dimmed">
                <strong>{campaign.googleAssetGroup?.pathTwo?.length || '0 '}/15</strong> This is the second part of the
                text that can be appended to the <strong>displayed URL</strong>. It follows path1 in the URL path.
                Continuing with the previous example, if path2 is set to "men", the display URL might look like
                https://example.com/shoes/men. <strong>This field can only be set when path1 is set.</strong>
              </Text>
            </Grid.Col>
          </Grid>
        </Card.Section>
      </Card>
    </>
  );
};

export default GoogleAssetGroupConfigurationTab;
